import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase';
import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  collection,
  getDocs,
  query,
  deleteField,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Modal,
  IconButton,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { styled } from '@mui/system';
import { alpha } from '@mui/material/styles';
import { Loader2, Edit, Trash2 } from 'lucide-react';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import { differenceInDays, addDays, isAfter } from 'date-fns';
import { Timestamp } from 'firebase/firestore';

// ────────────────────────────────────────────────
// Colors & Styled Components
// ────────────────────────────────────────────────
const colors = {
  primary: '#2ecc71',
  secondary: '#34495e',
  background: '#f5f7fa',
  accent: '#3498db',
  gradientStart: 'rgba(62,180,137,1)',
  gradientEnd: 'rgba(144,238,144,1)',
  warning: '#e67e22',
  danger: '#e74c3c',
};

const ModernCard = styled(Box)(({ theme }) => ({
  borderRadius: '16px',
  background: 'white',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
  padding: theme.spacing(2.5),
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
  },
}));

const CompactButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: '600',
  fontSize: '0.8125rem',
  padding: theme.spacing(0.5, 1.5),
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5, 1.25),
    fontSize: '0.75rem',
  },
}));

const ModalContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 460 },
  background: 'white',
  borderRadius: '20px',
  boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
  padding: theme.spacing(3),
  outline: 'none',
}));

const ScrollableTableWrapper = styled(Box)({
  width: '100%',
  overflowX: 'auto',
  '&::-webkit-scrollbar': {
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
  minWidth: { xs: '100%', md: 900 }, // force horizontal scroll on small screens
});

const EditUser = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [userData, setUserData] = useState({
    fullName: '',
    email: '',
    role: 'user',
    status: 'trial',
    plan: 'monthly',
    trialStartDate: null,
    invoiceUrl: '',
  });

  const [payments, setPayments] = useState([]);
  const [newPayment, setNewPayment] = useState({
    amount: '',
    plan: 'monthly',
    status: 'pending',
    invoiceFile: null,
  });

  const [replaceModalOpen, setReplaceModalOpen] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [trialModalOpen, setTrialModalOpen] = useState(false);
  const [trialDaysToAdd, setTrialDaysToAdd] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) {
          setSnackbar({ open: true, message: 'User not found', severity: 'error' });
          navigate('/admin/users');
          return;
        }

        const data = userSnap.data();
        setUserData({
          fullName: data.fullName || '',
          email: data.email || '',
          role: data.role || 'user',
          status: data.status || 'trial',
          plan: data.plan || 'monthly',
          trialStartDate: data.trialStartDate?.toDate() || null,
          invoiceUrl: data.invoiceUrl || '',
        });

        const paymentsQuery = query(collection(db, `users/${userId}/payments`));
        const paymentsSnap = await getDocs(paymentsQuery);
        setPayments(paymentsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
        setSnackbar({ open: true, message: 'Error loading data', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, navigate]);

  const handleUserChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setNewPayment((prev) => ({ ...prev, [name]: value }));
  };

  const handlePaymentFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf' || file.size > 5 * 1024 * 1024) {
      setSnackbar({ open: true, message: 'Invalid file (PDF, max 5MB)', severity: 'error' });
      return;
    }
    setNewPayment((prev) => ({ ...prev, invoiceFile: file }));
  };

  const handleSaveUser = async () => {
    setLoading(true);
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        fullName: userData.fullName.trim(),
        email: userData.email.trim(),
        role: userData.role,
        status: userData.status,
        plan: userData.plan,
        trialStartDate: userData.trialStartDate
          ? Timestamp.fromDate(userData.trialStartDate)
          : null,
      });
      setSnackbar({ open: true, message: 'User updated', severity: 'success' });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to update user', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddTrialDays = async () => {
    const days = parseInt(trialDaysToAdd, 10);
    if (isNaN(days) || days < 1) {
      setSnackbar({ open: true, message: 'Enter valid number of days', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const userRef = doc(db, 'users', userId);
      const snap = await getDoc(userRef);
      if (!snap.exists()) throw new Error('User not found');

      const data = snap.data();
      const now = new Date();
      let newStart;

      if (data.trialStartDate) {
        const oldStart = data.trialStartDate.toDate();
        const oldEnd = addDays(oldStart, 7);
        const remaining = Math.max(0, differenceInDays(oldEnd, now));
        const total = remaining + days;
        newStart = addDays(now, -(7 - total));
      } else {
        newStart = addDays(now, -(7 - days));
      }

      await updateDoc(userRef, { trialStartDate: Timestamp.fromDate(newStart) });

      setUserData((prev) => ({ ...prev, trialStartDate: newStart }));
      setSnackbar({ open: true, message: `Added ${days} trial day(s)`, severity: 'success' });
      setTrialModalOpen(false);
      setTrialDaysToAdd('');
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to update trial', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async () => {
    const amount = parseFloat(newPayment.amount);
    if (isNaN(amount) || amount <= 0) {
      setSnackbar({ open: true, message: 'Invalid amount', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const paymentId = `PAY-${Date.now()}`;
      let invoiceUrl = '';

      if (newPayment.invoiceFile) {
        const fileRef = ref(storage, `invoices/${userId}/${paymentId}_${newPayment.invoiceFile.name}`);
        await uploadBytes(fileRef, newPayment.invoiceFile);
        invoiceUrl = await getDownloadURL(fileRef);
      }

      const paymentData = {
        paymentId,
        amount,
        plan: newPayment.plan,
        status: newPayment.status,
        date: new Date().toISOString(),
        invoiceUrl,
      };

      await setDoc(doc(db, `users/${userId}/payments`, paymentId), paymentData);

      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        status: newPayment.status === 'completed' ? 'premium' : userData.status,
        plan: newPayment.plan,
        subscriptionStart: paymentData.date,
        subscriptionEnd: addDays(new Date(), newPayment.plan === 'termly' ? 90 : 365).toISOString(),
        invoiceUrl: invoiceUrl || userData.invoiceUrl,
      });

      setPayments((prev) => [{ id: paymentId, ...paymentData }, ...prev]);
      setUserData((prev) => ({
        ...prev,
        status: newPayment.status === 'completed' ? 'premium' : prev.status,
        plan: newPayment.plan,
        invoiceUrl: invoiceUrl || prev.invoiceUrl,
      }));

      setNewPayment({ amount: '', plan: 'monthly', status: 'pending', invoiceFile: null });
      setSnackbar({ open: true, message: 'Payment recorded', severity: 'success' });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to record payment', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleReplaceInvoice = (paymentId) => {
    setSelectedPaymentId(paymentId);
    setReplaceFile(null);
    setReplaceModalOpen(true);
  };

  const handleSaveReplaceInvoice = async () => {
    if (!replaceFile) {
      setSnackbar({ open: true, message: 'Please select a PDF file', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const file = replaceFile;
      const fileRef = ref(storage, `invoices/${userId}/${selectedPaymentId}_${file.name}`);
      await uploadBytes(fileRef, file);
      const newUrl = await getDownloadURL(fileRef);

      const paymentRef = doc(db, `users/${userId}/payments`, selectedPaymentId);
      await updateDoc(paymentRef, {
        invoiceUrl: newUrl,
        invoiceUpdatedAt: new Date().toISOString(),
      });

      await updateDoc(doc(db, 'users', userId), { invoiceUrl: newUrl });

      const paymentsQuery = query(collection(db, `users/${userId}/payments`));
      const snap = await getDocs(paymentsQuery);
      setPayments(snap.docs.map(d => ({ id: d.id, ...d.data() })));

      setSnackbar({ open: true, message: 'Invoice replaced', severity: 'success' });
      setReplaceModalOpen(false);
      setReplaceFile(null);
      setSelectedPaymentId(null);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to replace invoice', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvoice = (type, paymentId = null) => {
    setDeleteTarget({ type, paymentId });
    setDeleteDialogOpen(true);
  };

  const confirmDeleteInvoice = async () => {
    if (!deleteTarget) return;
    setLoading(true);
    try {
      if (deleteTarget.type === 'user') {
        await updateDoc(doc(db, 'users', userId), {
          invoiceUrl: deleteField(),
          invoiceUpdatedAt: deleteField(),
        });
        setUserData(prev => ({ ...prev, invoiceUrl: '' }));
      } else if (deleteTarget.type === 'payment' && deleteTarget.paymentId) {
        await updateDoc(doc(db, `users/${userId}/payments`, deleteTarget.paymentId), {
          invoiceUrl: deleteField(),
          invoiceUpdatedAt: deleteField(),
        });
        const q = query(collection(db, `users/${userId}/payments`));
        const snap = await getDocs(q);
        setPayments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
      setSnackbar({ open: true, message: 'Invoice removed', severity: 'success' });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to delete invoice', severity: 'error' });
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  const getTrialDaysRemaining = (user) => {
    if (user.status !== 'trial' || !user.trialStartDate) return 'N/A';
    const end = addDays(user.trialStartDate.toDate(), 7);
    const now = new Date();
    return isAfter(now, end) ? 'Expired' : differenceInDays(end, now);
  };

  // ────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────
  if (loading && !userData.email) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress color="success" />
      </Box>
    );
  }

  return (
    <div style={{ background: colors.background, minHeight: '100vh' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 }, mt: { xs: 2, md: 4 }, px: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <ModernCard sx={{ mb: { xs: 3, md: 5 }, background: `linear-gradient(145deg, ${colors.gradientStart}, ${colors.gradientEnd})`, color: 'white' }}>
          <Box sx={{ p: { xs: 2.5, md: 4 } }}>
            <Typography variant="h5" fontWeight="700" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <PersonIcon fontSize="large" />
              Edit User: {userData.fullName || 'Loading...'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Manage profile, subscription, trial, payments & invoices
            </Typography>
          </Box>
        </ModernCard>

        {/* User Details */}
        <ModernCard sx={{ mb: 4 }}>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" color={colors.secondary} fontWeight={600} mb={2.5}>
              User Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Full Name" name="fullName" value={userData.fullName} onChange={handleUserChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Email" value={userData.email} disabled />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Role</InputLabel>
                  <Select name="role" value={userData.role} label="Role" onChange={handleUserChange}>
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select name="status" value={userData.status} label="Status" onChange={handleUserChange}>
                    <MenuItem value="trial">Trial</MenuItem>
                    <MenuItem value="premium">Premium</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Plan</InputLabel>
                  <Select name="plan" value={userData.plan} label="Plan" onChange={handleUserChange}>
                    <MenuItem value="monthly">Monthly (Rs 300/mo)</MenuItem>
                    <MenuItem value="termly">Termly – 3 months (Rs 750)</MenuItem>
                    <MenuItem value="yearly">Yearly (Rs 2500)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {userData.status === 'trial' && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color={colors.secondary}>
                      Trial days left: <strong>{getTrialDaysRemaining(userData)}</strong>
                    </Typography>
                    <CompactButton
                      variant="contained"
                      size="small"
                      sx={{ bgcolor: colors.primary, fontSize: '0.8rem' }}
                      onClick={() => setTrialModalOpen(true)}
                    >
                      Extend
                    </CompactButton>
                  </Box>
                </Grid>
              )}

              {userData.invoiceUrl && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Latest Invoice:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                    <a
                      href={userData.invoiceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: colors.accent, fontSize: '0.875rem', fontWeight: 500 }}
                    >
                      View PDF
                    </a>
                    <CompactButton
                      variant="outlined"
                      size="small"
                      color="error"
                      startIcon={<Trash2 size={14} />}
                      onClick={() => handleDeleteInvoice('user')}
                      sx={{ fontSize: '0.75rem', minWidth: 'auto' }}
                    >
                      Delete
                    </CompactButton>
                  </Box>
                </Grid>
              )}
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <CompactButton variant="outlined" size="small" onClick={() => navigate('/admin/users')}>
                Cancel
              </CompactButton>
              <CompactButton
                variant="contained"
                size="small"
                sx={{ bgcolor: colors.primary }}
                onClick={handleSaveUser}
                disabled={loading}
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : 'Save'}
              </CompactButton>
            </Box>
          </Box>
        </ModernCard>

        {/* Trial Modal */}
        <Modal open={trialModalOpen} onClose={() => setTrialModalOpen(false)}>
          <ModalContent>
            <Typography variant="h6" gutterBottom>
              Extend Trial
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Additional Days"
              type="number"
              value={trialDaysToAdd}
              onChange={(e) => setTrialDaysToAdd(e.target.value)}
              inputProps={{ min: 1 }}
              sx={{ mt: 2 }}
            />
            <Box sx={{ mt: 3, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={() => setTrialModalOpen(false)}>Cancel</Button>
              <CompactButton size="small" variant="contained" onClick={handleAddTrialDays}>
                Add
              </CompactButton>
            </Box>
          </ModalContent>
        </Modal>

        {/* Replace Invoice Modal */}
        <Modal open={replaceModalOpen} onClose={() => setReplaceModalOpen(false)}>
          <ModalContent sx={{ width: 460 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
              <Typography variant="h6" fontWeight={700} color={colors.secondary}>
                Replace Invoice
              </Typography>
              <IconButton size="small" onClick={() => setReplaceModalOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Typography variant="body2" color="text.secondary" mb={2}>
              Upload new PDF for this payment
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                New Invoice (PDF, max 5MB)
              </Typography>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file && file.type === 'application/pdf' && file.size <= 5 * 1024 * 1024) {
                    setReplaceFile(file);
                  } else {
                    setSnackbar({ open: true, message: 'Invalid file (PDF only, max 5MB)', severity: 'error' });
                  }
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
              <Button size="small" variant="outlined" onClick={() => setReplaceModalOpen(false)}>
                Cancel
              </Button>
              <CompactButton
                size="small"
                variant="contained"
                disabled={loading || !replaceFile}
                onClick={handleSaveReplaceInvoice}
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : 'Replace'}
              </CompactButton>
            </Box>
          </ModalContent>
        </Modal>

        {/* Delete Confirmation */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this invoice? This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button size="small" onClick={confirmDeleteInvoice} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payment History – now scrollable */}
        <ModernCard sx={{ mb: 4 }}>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" color={colors.secondary} fontWeight={600} mb={2}>
              Payment History
            </Typography>

            <ScrollableTableWrapper>
              <DataGrid
                rows={payments}
                columns={[
                  { field: 'paymentId', headerName: 'ID', width: 140 },
                  { field: 'amount', headerName: 'Amount', width: 110 },
                  { field: 'plan', headerName: 'Plan', width: 110 },
                  {
                    field: 'date',
                    headerName: 'Date',
                    width: 140,
                    valueGetter: ({ value }) => new Date(value).toLocaleDateString(),
                  },
                  { field: 'status', headerName: 'Status', width: 100 },
                  {
                    field: 'invoiceUrl',
                    headerName: 'Invoice',
                    width: 100,
                    renderCell: ({ value }) =>
                      value ? (
                        <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: colors.accent, fontSize: '0.875rem' }}>
                          View
                        </a>
                      ) : (
                        '—'
                      ),
                  },
                  {
                    field: 'actions',
                    headerName: 'Actions',
                    width: 230,
                    renderCell: (params) => (
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {params.row.invoiceUrl && (
                          <CompactButton
                            variant="outlined"
                            size="small"
                            color="error"
                            startIcon={<Trash2 size={14} />}
                            onClick={() => handleDeleteInvoice('payment', params.row.id)}
                          >
                            Delete
                          </CompactButton>
                        )}
                        <CompactButton
                          variant="outlined"
                          size="small"
                          startIcon={<Edit size={14} />}
                          onClick={() => handleReplaceInvoice(params.row.id)}
                          sx={{ borderColor: colors.accent, color: colors.accent }}
                        >
                          Replace
                        </CompactButton>
                      </Box>
                    ),
                  },
                ]}
                pageSizeOptions={[5, 10]}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-cell': { py: 0.75 },
                  minWidth: 900, // forces horizontal scroll when screen is smaller
                }}
                autoHeight
              />
            </ScrollableTableWrapper>
          </Box>
        </ModernCard>

        {/* Add New Payment */}
        <ModernCard>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" color={colors.secondary} fontWeight={600} mb={2.5}>
              Record New Payment
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Amount (Rs)"
                  name="amount"
                  type="number"
                  value={newPayment.amount}
                  onChange={handlePaymentChange}
                  placeholder="750 for Termly"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Plan</InputLabel>
                  <Select name="plan" value={newPayment.plan} label="Plan" onChange={handlePaymentChange}>
                    <MenuItem value="monthly">Monthly (Rs 300)</MenuItem>
                    <MenuItem value="termly">Termly – 3 months (Rs 750)</MenuItem>
                    <MenuItem value="yearly">Yearly (Rs 2500)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select name="status" value={newPayment.status} label="Status" onChange={handlePaymentChange}>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Upload Invoice (PDF, max 5MB)
                </Typography>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handlePaymentFileChange}
                  style={{ marginTop: 6 }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <CompactButton
                variant="contained"
                size="small"
                sx={{ bgcolor: colors.primary }}
                onClick={handleAddPayment}
                disabled={loading}
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : 'Add Payment'}
              </CompactButton>

              <CompactButton
                variant="outlined"
                size="small"
                onClick={() =>
                  setNewPayment({ amount: '', plan: 'monthly', status: 'pending', invoiceFile: null })
                }
              >
                Clear
              </CompactButton>
            </Box>
          </Box>
        </ModernCard>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </div>
  );
};

export default EditUser;